import { FC, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactComponent as ReloadIcon } from 'src/assets/svg/Reload.svg'
import { Skeleton } from 'src/components/Skeleton'
import { StatusModal } from 'src/components/StatusModal'
import {
  AI_LISTENING_BRAND_PROFILE_ROUTE,
  AI_LISTENING_PROMPTS_ROUTE,
} from 'src/constants/routes'
import {
  useAiListeningDateRange,
  useAiListeningLink,
} from 'src/hooks/useAiListeningDateRange'
import { useGetAiListeningPromptDeepDiveQuery } from 'src/reactQuery/useGetAiListeningPromptDeepDiveQuery'
import { CitationIntelligence } from './CitationIntelligence'
import { CompetitivePosition } from './CompetitivePosition'
import { DeepDiveInsights } from './DeepDiveInsights'
import { EngineCards } from './EngineCards'
import { OutcomeSummary } from './OutcomeSummary'
import { OutcomeTable } from './OutcomeTable'
import { ReloadHistory } from './ReloadHistory'
import {
  AttributeOwnership,
  PerceptionClusters,
  TopicClusters,
} from './TopicAttributes'
import { useReloadPrompt } from './useReloadPrompt'
import styles from './deepDive.module.scss'
import { StateCard } from '../shared/StateCard'
import { AiListeningPage, PageBreadcrumb } from '../shared/Layout'

// /ai-listening/prompts/:promptId — one prompt's latest run in the top-bar
// date range in depth, and every run in that range in Reload History.
export const PromptDeepDivePage: FC = () => {
  const { promptId = '' } = useParams<{ promptId: string }>()
  const navigate = useNavigate()
  const link = useAiListeningLink()
  const range = useAiListeningDateRange()
  // A Reload History run; undefined = the latest in the range.
  const [selectedRun, setSelectedRun] = useState<string>()

  const { data, isError, isLoading } = useGetAiListeningPromptDeepDiveQuery(
    promptId,
    undefined,
    range
  )
  // A run picked before the range changed may no longer be listed.
  const isLatestRun =
    !selectedRun ||
    selectedRun === data?.checkedAt ||
    !data?.checks.some((check) => check.checkedAt === selectedRun)
  const { data: runData, isPlaceholderData: isRunPlaceholder } =
    useGetAiListeningPromptDeepDiveQuery(
      isLatestRun ? undefined : promptId,
      selectedRun,
      range
    )
  const shownRun = isLatestRun ? data?.checkedAt : selectedRun
  const reload = useReloadPrompt(promptId)

  const profileLink = data
    ? link(AI_LISTENING_BRAND_PROFILE_ROUTE, {
        profileId: data.prompt.brandProfile,
      })
    : link(AI_LISTENING_PROMPTS_ROUTE)

  const header = (
    <>
      <PageBreadcrumb
        fallbackTo={profileLink}
        items={['AI Listening', 'Brand Profile', 'Prompt']}
      />
      <div>
        <h1 className={styles.pageTitle}>Prompt Deep-Dive Investigation</h1>
        <p className={styles.pageSubtitle}>
          Individual monitored prompts and deep analysis
        </p>
      </div>
      <div className={styles.pageActions}>
        <button
          className={styles.backLink}
          onClick={() => navigate(profileLink)}
          type="button"
        >
          <svg fill="none" height="20" viewBox="0 0 20 20" width="20">
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
            />
          </svg>
          Back to Brand Profile
        </button>
        <button
          className={styles.reloadButton}
          disabled={reload.isDisabled || !data}
          onClick={reload.reload}
          type="button"
        >
          {reload.label}
          <ReloadIcon className={styles.reloadIcon} />
        </button>
      </div>
    </>
  )

  if (isLoading) {
    return (
      <AiListeningPage>
        {header}
        <Skeleton height={360} radius={20} width="100%" />
        <Skeleton height={520} radius={20} width="100%" />
      </AiListeningPage>
    )
  }

  if (isError || !data) {
    return (
      <AiListeningPage>
        {header}
        <StateCard
          action={{
            label: 'Go to Prompts',
            onClick: () => navigate(link(AI_LISTENING_PROMPTS_ROUTE)),
          }}
          description="This prompt doesn't exist or you no longer have access to it."
          icon="prompt"
          title="Prompt not found"
        />
      </AiListeningPage>
    )
  }

  const totalModels = data.tiles.visibility.of || data.engines.length
  const youName =
    data.competitivePosition.brands.find((brand) => brand.label === 'you')
      ?.name ?? 'your brand'
  const isCollecting = data.prompt.checkState === 'pending' || reload.isPending
  // Runs exist, just none in the selected date range.
  const isPeriodEmpty =
    !!range && !data.checkedAt && !!data.prompt.lastCheckedAt

  return (
    <AiListeningPage>
      {header}

      {!data.hasData ? (
        <StateCard
          description={
            isCollecting
              ? 'Every AI model is being asked this prompt. The first results arrive in a few minutes.'
              : isPeriodEmpty
                ? 'This prompt was not run in the selected period. Pick a wider date range to see earlier runs.'
                : data.prompt.status === 'draft'
                  ? 'This prompt is a draft. Activate it from the Prompts page to start collecting AI answers.'
                  : 'There are no AI answers for this prompt yet. Reload it to ask every model now.'
          }
          icon="processing"
          title={
            isCollecting
              ? 'Collecting AI answers'
              : isPeriodEmpty
                ? 'No runs in this period'
                : 'No results yet'
          }
        />
      ) : (
        <>
          <section className={styles.card}>
            <OutcomeSummary data={data} />
          </section>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>
              AI Engine Responses Comparison (Monitored Engines)
            </h2>
            <EngineCards engines={data.engines} />
          </section>

          <CompetitivePosition
            position={data.competitivePosition}
            totalModels={totalModels}
          />

          <TopicClusters clusters={data.topicClusters} />

          <div className={styles.twoColumns}>
            <AttributeOwnership attributes={data.attributeOwnership} />
            <PerceptionClusters
              brandName={youName}
              perception={data.perception}
            />
          </div>

          <CitationIntelligence
            brandName={youName}
            citations={data.citations}
            domain={data.domain}
          />

          <OutcomeTable
            leaderKey={data.outcome?.leader}
            rows={data.outcomeTable}
            totalModels={totalModels}
          />
        </>
      )}

      {shownRun && (
        <DeepDiveInsights checkedAt={shownRun} promptId={promptId} />
      )}

      <ReloadHistory
        checks={data.checks}
        emptyText={
          isPeriodEmpty
            ? 'No runs in the selected period.'
            : 'No runs yet. The first results arrive a few minutes after a prompt is added.'
        }
        isLoading={!isLatestRun && (!runData || isRunPlaceholder)}
        onSelect={(checkedAt) =>
          setSelectedRun(
            checkedAt === data.checks[0]?.checkedAt ? undefined : checkedAt
          )
        }
        run={isLatestRun ? data : runData}
        selectedCheckedAt={isLatestRun ? undefined : selectedRun}
      />

      <StatusModal
        closeOnBackdrop
        hideCountdown
        isOpen={reload.modal.isOpen}
        message={reload.modal.message}
        onClose={reload.modal.onClose}
        title={reload.modal.title}
        type={reload.modal.type}
      />
    </AiListeningPage>
  )
}
